import React, { useCallback, useState } from 'react'
import cn from 'classnames'
import { UploadButton, UploadedFileInfo, UploadButtonError } from '@grammarly/website-components'
import { Button, Tabs, Typography } from '@grammarly/badger-ui'
import { FormattedMessage, useIntl } from 'react-intl'

import { ContentProps } from '../../../renderer'
import { TypeComponentTextInput } from '../../../../lib/types/component-text-input'
import { ExpandedTextarea } from '../expanded-text-area'
import { ErrorText, FormControl, Label } from '../../../ui-lib/form'
import { IconDelete } from '../../../ui-lib/icons/delete'
import { useApiService } from '../../../../lib/hooks'

import styles from './file-upload-or-text-input-tabs.module.scss'
import { useFormContext } from 'react-hook-form'
import { useError } from '../../../inputs/hooks'
import { FULLSTORY_MASK_CLASSNAME } from '../../../../lib/tracking/fullstory/constants'

const getUploadErrorText = (
  intl: ReturnType<typeof useIntl>,
  error: UploadButtonError,
  maxFileSize?: number,
  userFileSize?: number
) => {
  const fileTypeError = intl.formatMessage({
    id: 'goTemplatesTool.fileUploadOrTextInputTabs.error.fileType',
    description: 'Go Templates Tool - File Upload Or Text Input Tabs - Error - File Type',
    defaultMessage: 'File type not supported. Please upload a .doc, .docx, or .txt.',
  })
  const unknownError = intl.formatMessage({
    id: 'goTemplatesTool.fileUploadOrTextInputTabs.error.generic',
    description: 'Go Templates Tool - File Upload Or Text Input Tabs - Error - Generic',
    defaultMessage: 'There was an error uploading your file.',
  })

  switch (error) {
    case 'FILE_ERROR_INVALID_EXT':
      return fileTypeError
    case 'FILE_ERROR_TOO_BIG': {
      const maxFileSizeMB = typeof maxFileSize === 'number' ? (maxFileSize / 1000000).toFixed(2) : '0.00'
      const userFileSizeMB = typeof userFileSize === 'number' ? (userFileSize / 1000000).toFixed(2) : '0.00'

      return intl.formatMessage(
        {
          id: 'goTemplatesTool.fileUploadOrTextInputTabs.error.tooLarge',
          description: 'Go Templates Tool - File Upload Or Text Input Tabs - Error - Too Large',
          defaultMessage:
            'The file size of {userFileSizeMB} MB exceeds the {maxFileSizeMB} MB limit. Please upload a smaller file.',
        },
        { userFileSizeMB, maxFileSizeMB }
      )
    }
    case 'UNKNOWN_ERROR':
      return unknownError
    default:
      return unknownError
  }
}

const UploadedFileName: React.FC<{ fileName: string; onRemoveFileClick: () => void }> = (props) => {
  const { fileName, onRemoveFileClick } = props

  return (
    <div className={cn(FULLSTORY_MASK_CLASSNAME, styles.uploadedFileName)}>
      <Typography.C4 as="span">{fileName}</Typography.C4>
      <Button
        padding="compact"
        className={styles.removeButton}
        onClick={onRemoveFileClick}
        iconLeft={<IconDelete color="blue60" />}
      >
        <FormattedMessage
          id="global.uiAction.remove"
          description="Text for CTA that prompts user to remove an item"
          defaultMessage="Remove"
        />
      </Button>
    </div>
  )
}

export interface FileUploadOrTextInputTabsProps extends ContentProps<TypeComponentTextInput> {
  activeTab: number
  onTabChange: (value: React.SetStateAction<number>) => void
  onFileUploadSuccess?: () => void
  onFileUploadError?: (error: UploadButtonError, fileName?: string) => void
  onRemoveFile?: () => void
  onPasteTextInput?: () => void
  labelClassName: string
  textareaClassName: string
  helperTextClassName: string
  isDisabled?: boolean
  onChange?: () => void
}

export const FileUploadOrTextInputTabs: React.FC<FileUploadOrTextInputTabsProps> = ({
  activeTab,
  onTabChange,
  onFileUploadSuccess,
  onFileUploadError,
  onRemoveFile,
  onPasteTextInput,
  labelClassName,
  structure,
  helperTextClassName,
  textareaClassName,
  isDisabled,
  onChange,
}) => {
  const intl = useIntl()
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [fileUploadError, setFileUploadError] = useState('')
  const { dox } = useApiService()
  const { setValue, getValues, clearErrors } = useFormContext()

  const isUploadTab = activeTab === 0
  const isAddTextTab = activeTab === 1

  const {
    fields: { label, ...otherFields },
    ...textInputStructure
  } = structure
  const { name, isRequired, maxLength = 3000 } = otherFields

  const uploadLabel = intl.formatMessage(
    {
      id: 'goTemplatesTool.fileUploadOrTextInputTabs.uploadLabel',
      description: 'Go Templates Tool - File Upload Or Text Input Tabs - Upload Label',
      defaultMessage: 'Upload {label}',
    },
    { label }
  )
  const addTextLabel = intl.formatMessage(
    {
      id: 'goTemplatesTool.fileUploadOrTextInputTabs.addTextLabel',
      description: 'Go Templates Tool - File Upload Or Text Input Tabs - Add Text Label',
      defaultMessage: 'Add {label}',
    },
    { label }
  )

  const uploadCtaText = intl.formatMessage({
    id: 'global.uiAction.uploadFile',
    description: 'Text for CTA that prompts user to upload a file',
    defaultMessage: 'Upload file',
  })

  const formFieldError = useError(name)

  const onFileChangeFinish = useCallback(
    (text: string, fileInfo: Partial<UploadedFileInfo>) => {
      onFileUploadSuccess?.()

      const { fileName } = fileInfo
      if (fileName) {
        setUploadedFileName(fileName)
      }
      let trimmedText = text.trim()
      if (trimmedText.length > maxLength) {
        trimmedText = text.slice(0, maxLength)
      }
      setValue(name, trimmedText)
      setFileUploadError('')
      clearErrors(name)
    },
    [name, onFileUploadSuccess, setValue, clearErrors, maxLength]
  )

  const onRemoveFileClick = useCallback(() => {
    onRemoveFile?.()
    setUploadedFileName('')
    setValue(name, '')
    setFileUploadError('')
  }, [onRemoveFile, setValue, name])

  const onUploadError = (error: UploadButtonError, fileName?: string, maxFileSize?: number, userFileSize?: number) => {
    onFileUploadError?.(error, fileName)
    setUploadedFileName('')
    setValue(name, '')
    setFileUploadError(getUploadErrorText(intl, error, maxFileSize, userFileSize))
  }

  const isUploadTabDisabled = isDisabled || (isAddTextTab && !!getValues(name))
  const isTextTabDisabled = isDisabled || (isUploadTab && !!getValues(name))

  return (
    <>
      <Label isRequired={isRequired} className={labelClassName}>
        {isUploadTab ? uploadLabel : addTextLabel}
      </Label>
      <Tabs index={activeTab} onChange={onTabChange} id="controlled-tabs" className={styles.tabs}>
        <Tabs.TabList className={styles.tabList}>
          <Tabs.Tab disabled={isUploadTabDisabled}>{uploadLabel}</Tabs.Tab>
          <Tabs.Tab disabled={isTextTabDisabled}>{addTextLabel}</Tabs.Tab>
        </Tabs.TabList>
        <Tabs.TabPanels>
          <Tabs.TabPanel className={styles.tabPanel}>
            <FormControl
              name={name}
              isRequired={isRequired}
              isInvalid={!!formFieldError}
              error={formFieldError}
              isDisabled={isDisabled}
            >
              {uploadedFileName ? (
                <UploadedFileName fileName={uploadedFileName} onRemoveFileClick={onRemoveFileClick} />
              ) : (
                <div className={styles.uploadTab}>
                  <Typography.C4 as="div" className={cn(helperTextClassName, styles.helpText)}>
                    <FormattedMessage
                      id="goTemplatesTool.fileUploadOrTextInputTabs.error.fileFormat"
                      description="Go Templates Tool - File Upload Or Text Input Tabs - Error - File Format"
                      defaultMessage="Accepted file formats: DOC, DOCX, and TXT"
                    />
                  </Typography.C4>
                  <UploadButton
                    doxClient={dox}
                    onFileChangeFinish={(text, fileInfo) => {
                      onFileChangeFinish(text, fileInfo)
                    }}
                    onError={onUploadError}
                    text={uploadCtaText}
                    aria-label={uploadCtaText}
                    className={styles.uploadButton}
                    isDisabled={isDisabled}
                    onClick={onChange}
                  />
                  <Typography.C4 as="div" aria-live="polite" className={styles.fileUploadError}>
                    {fileUploadError ? fileUploadError : null}
                  </Typography.C4>
                </div>
              )}
              <ErrorText />
            </FormControl>
          </Tabs.TabPanel>
          <Tabs.TabPanel className={styles.tabPanel}>
            <ExpandedTextarea
              structure={{ fields: otherFields, ...textInputStructure }}
              isShown={isAddTextTab}
              className={textareaClassName}
              helperTextClassName={cn(helperTextClassName, styles.helpText)}
              onPaste={onPasteTextInput}
              isDisabled={isDisabled}
              onChange={onChange}
            />
          </Tabs.TabPanel>
        </Tabs.TabPanels>
      </Tabs>
    </>
  )
}
